/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { MessageFileItem } from '../../../api/chat/chat.types';
import { getFileMeta, getPreviewKind } from './conversation.hooks';
import { filesApi } from '../../../api/files/files';
import {
  FileCard,
  FileIcon,
  FileTextCol,
  FileName,
  FileMeta,
  PreviewWrap,
  PreviewImg,
  PreviewVideo,
  PreviewShimmer,
  DownloadBtn,
  FileDownloadBtn,
} from './conversation.styled';

/* ── signed-URL cache (survives re-renders, cleared on page unload) ── */
const urlCache = new Map<string, string>();

const DownloadIcon: React.FC = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 2v8m0 0L5 7m3 3l3-3M3 12h10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Image preview with smooth shimmer→image transition ── */
const ImagePreview: React.FC<{
  url: string | null;
  loading: boolean;
  alt: string;
  onError: () => void;
  onDownload: () => void;
}> = ({ url, loading, alt, onError, onDownload }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const showShimmer = loading || !imgLoaded;

  return (
    <PreviewWrap>
      {showShimmer && <PreviewShimmer />}
      {url && (
        <PreviewImg
          src={url}
          alt={alt}
          loading="lazy"
          onLoad={() => setImgLoaded(true)}
          onError={onError}
          style={{
            position: showShimmer ? 'absolute' : 'relative',
            top: 0,
            left: 0,
            opacity: imgLoaded ? 1 : 0,
            transition: 'opacity 0.2s ease-in',
          }}
        />
      )}
      {!showShimmer && (
        <DownloadBtn type="button" onClick={onDownload} aria-label="Download">
          <DownloadIcon />
        </DownloadBtn>
      )}
    </PreviewWrap>
  );
};

/* ── Video preview with smooth shimmer→video transition ── */
const VideoPreview: React.FC<{
  url: string | null;
  loading: boolean;
  onError: () => void;
  onDownload: () => void;
}> = ({ url, loading, onError, onDownload }) => {
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const showShimmer = loading || !videoReady;

  // Handle case where metadata loaded from cache before handler attached
  useEffect(() => {
    const vid = videoRef.current;
    if (vid && vid.readyState >= 1) {
      setVideoReady(true);
    }
  }, [url]);

  return (
    <PreviewWrap>
      {showShimmer && <PreviewShimmer />}
      {url && (
        <PreviewVideo
          ref={videoRef}
          src={url}
          controls
          preload="metadata"
          playsInline
          onLoadedMetadata={() => setVideoReady(true)}
          onError={onError}
          style={{
            position: showShimmer ? 'absolute' : 'relative',
            top: 0,
            left: 0,
            opacity: videoReady ? 1 : 0,
            transition: 'opacity 0.2s ease-in',
          }}
        />
      )}
      {!showShimmer && (
        <DownloadBtn type="button" onClick={onDownload} aria-label="Download">
          <DownloadIcon />
        </DownloadBtn>
      )}
    </PreviewWrap>
  );
};

type Props = {
  file: MessageFileItem;
};

export const FileAttachment: React.FC<Props> = ({ file }) => {
  const kind = getPreviewKind(file.name, file.file_type);

  /* ── Deleted file placeholder ── */
  if (file.deleted_at) {
    const label =
      kind === 'image' ? 'Deleted image'
      : kind === 'video' ? 'Deleted video'
      : 'Deleted file';
    return (
      <FileCard style={{ opacity: 0.5 }}>
        <FileIcon src="/file.svg" alt="" aria-hidden="true" />
        <FileTextCol>
          <FileName style={{ fontStyle: 'italic', color: '#b7bdcb' }}>{label}</FileName>
        </FileTextCol>
      </FileCard>
    );
  }

  const needsUrl = kind !== null; // images, videos, PDFs need a signed URL

  const [url, setUrl] = useState<string | null>(() => urlCache.get(file.id) ?? null);
  const [loading, setLoading] = useState(needsUrl && !url);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!needsUrl) return;
    if (url) return; // already have it

    let cancelled = false;

    (async () => {
      try {
        const signed = await filesApi.getFileUrl(file.id);
        if (!cancelled) {
          urlCache.set(file.id, signed);
          setUrl(signed);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [file.id, needsUrl, url]);

  const handleDownload = useCallback(async () => {
    await filesApi.downloadViaProxy(file.id, file.name);
  }, [file.id, file.name]);

  /* ── Image preview ── */
  if (kind === 'image') {
    if (error || (!loading && !url)) return <FallbackCard file={file} onDownload={handleDownload} />;
    return (
      <ImagePreview
        url={url}
        loading={loading}
        alt={file.name}
        onError={() => setError(true)}
        onDownload={handleDownload}
      />
    );
  }

  /* ── Video preview ── */
  if (kind === 'video') {
    if (error || (!loading && !url)) return <FallbackCard file={file} onDownload={handleDownload} />;
    return (
      <VideoPreview
        url={url}
        loading={loading}
        onError={() => setError(true)}
        onDownload={handleDownload}
      />
    );
  }

  /* ── PDF — open in new tab on tap ── */
  if (kind === 'pdf') {
    return <FallbackCard file={file} onDownload={handleDownload} icon="/file.svg" />;
  }

  /* ── Generic file card ── */
  return <FallbackCard file={file} onDownload={handleDownload} />;
};

/* ── Shared file card with download button ── */

type FallbackProps = {
  file: MessageFileItem;
  onDownload: () => void;
  icon?: string;
};

const FallbackCard: React.FC<FallbackProps> = ({ file, onDownload, icon = '/file.svg' }) => {
  const meta = getFileMeta(file);

  return (
    <FileCard>
      <FileIcon src={icon} alt="" aria-hidden="true" />
      <FileTextCol>
        <FileName>{String(file.name || 'File')}</FileName>
        <FileMeta>
          {meta.typeLabel} {meta.sizeLabel !== '0B' ? `\u2022 ${meta.sizeLabel}` : ''}
        </FileMeta>
      </FileTextCol>
      <FileDownloadBtn type="button" onClick={onDownload} aria-label="Download">
        <DownloadIcon />
      </FileDownloadBtn>
    </FileCard>
  );
};
